/** Processes a graphql string
 * (in actuality this does absolutely nothing and is just a useful way of denoting a graphql query and enables editor syntax highlighting)
 */
function gql(s, ...variables) {
  return s.map((s, i) => {
    if (i === 0) {
      return s;
    }
    return variables[i - 1] + s;
  }).join('');
}

const globalFetch = typeof fetch === 'undefined' ? undefined : fetch;
class QueryError extends Error {
  constructor(response, status, data, message) {
    super(message);
    this.response = response;
    this.status = status;
    this.data = data;
  }
}
const getBody = ({
  sourceData,
  stringify
}) => {
  if (sourceData == null) {
    return undefined;
  }
  if (sourceData instanceof FormData) {
    return sourceData;
  }
  if (typeof sourceData === 'string') {
    return sourceData;
  }
  return stringify(sourceData);
};
const getSearchParams = ({
  search,
  query: sourceData
}) => {
  let searchParams = new URLSearchParams(search);
  if (typeof sourceData === 'string' && sourceData) {
    if (sourceData.startsWith('?')) {
      searchParams = new URLSearchParams(sourceData);
    } else {
      searchParams = new URLSearchParams('?' + sourceData);
    }
  } else if (sourceData) {
    Object.entries(sourceData).forEach(([key, value]) => {
      if (value == null) {
        return;
      }
      if (Array.isArray(value)) {
        value.forEach(v => {
          if (v == null) {
            return;
          }
          searchParams.append(key, v);
        });
      } else {
        searchParams.set(key, value);
      }
    });
  }
  return searchParams;
};
const resolveUrl = url => {
  if (!url) {
    return;
  }
  if (url.startsWith('/') && typeof window !== 'undefined') {
    return new URL(`${window.location.origin}${url}`);
  }
  return new URL(url);
};
const query = async args => {
  const {
    url: baseUrl,
    headers = {},
    method = 'GET',
    maxAttempts = 3,
    attempt = 1,
    fetch = globalFetch,
    parse = JSON.parse,
    stringify = JSON.stringify,
    data: _data,
    query: _query,
    ...requestInit
  } = args;
  const sourceData = method === 'GET' ? undefined : _data;
  const sourceQuery = method === 'GET' ? _query || _data : _query;
  if (fetch == null) {
    throw new Error('Could not find fetch api. You may need to import a polyfill');
  }
  const urls = Array.isArray(baseUrl) ? baseUrl : [baseUrl];
  if (!urls.some(Boolean)) {
    throw new Error('No URL provided');
  }
  while (urls.length) {
    try {
      const uri = resolveUrl(urls.shift());
      if (!uri) {
        continue;
      }
      uri.search = getSearchParams({
        query: sourceQuery,
        search: uri.search
      }).toString();
      const body = getBody({
        sourceData,
        stringify
      });
      if (!headers['Content-Type'] && method !== 'GET') {
        if (sourceData instanceof FormData) {
          // Don't need to set the content type for a raw FormData instance
          // headers['Content-Type'] = 'multipart/form-data';
        } else if (sourceData && typeof sourceData === 'object') {
          headers['Content-Type'] = 'application/json';
        }
      }
      const response = await fetch(uri.toString(), {
        method,
        body,
        headers,
        ...requestInit
      });
      if (response.status >= 500 && response.status <= 599 && attempt < maxAttempts) {
        await new Promise(res => setTimeout(res, 1000));
        return query({
          ...args,
          attempt: attempt + 1
        });
      }
      const contentType = response.headers.get('content-type');
      if (!response.ok) {
        const text = await response.text();
        if (contentType?.includes('application/json')) {
          const json = parse(text);
          throw new QueryError(response, response.status, json, `Error fetching ${uri}`);
        } else {
          throw new QueryError(response, response.status, {}, text);
        }
      }
      const text = await response.text();
      if (contentType?.includes('application/json')) {
        return parse(text);
      } else {
        return text;
      }
    } catch (e) {
      if (!urls.length) {
        throw e;
      }
    }
  }
  throw new Error();
};
var sendQuery = query;

const normalizeIfAddress = t => {
  if (typeof t === 'string' && t.toLowerCase().startsWith('0x')) {
    return t.toLowerCase();
  }
  return t;
};

const defaultSendQuery = sendQuery;
const interpolateQuery = (query, variables) => {
  return Object.entries(variables).reduce((query, [key, value]) => {
    if (value == null) {
      return query;
    }
    value = normalizeIfAddress(value);
    value = JSON.stringify(value);
    return query.replace(`$${key}`, value);
  }, query);
};
const formatQuery = ({
  query,
  variables
}) => {
  if (typeof query !== 'string') {
    query = query.toString();
  }
  if (variables) {
    query = interpolateQuery(query, variables);
  }
  return query;
};
const handleErrors = errors => {
  // If there was an error with the query, we'll receive an array of errors
  if (errors?.[0]?.message) {
    throw new Error(errors[0].message);
  }
  // Potentially a more generic error (like the endpoint was down)
  if (errors?.message) {
    throw new Error(errors.message);
  }
};
const sendGraphQuery = async ({
  url,
  query,
  args: variables,
  sendQuery,
  headers = {},
  ...rest
}) => {
  const {
    data,
    errors
  } = await sendQuery({
    url,
    cache: 'no-cache',
    headers: {
      'Content-Type': 'application/json',
      ...headers
    },
    method: 'POST',
    data: {
      query,
      variables
    },
    ...rest
  });
  handleErrors(errors);
  return data;
};
async function queryGraph({
  query,
  url,
  sendQuery = defaultSendQuery,
  variables,
  args,
  ...rest
}) {
  if (!fetch) {
    throw new Error('Could not find fetch api. You may need to import a polyfill');
  }
  return sendGraphQuery({
    url,
    query: formatQuery({
      query,
      variables
    }),
    fetch,
    sendQuery,
    args,
    ...rest
  });
}

const createWhereField = fieldName => {
  const createOperator = operator => v => {
    if (typeof v === 'function') {
      v = v(createWhere());
    }
    return [fieldName, operator, v];
  };
  const whereField = createOperator('is');
  whereField.is = whereField;
  whereField.ne = createOperator('ne');
  whereField.not = createOperator('not');
  whereField.gt = createOperator('gt');
  whereField.gte = createOperator('gte');
  whereField.lt = createOperator('lt');
  whereField.lte = createOperator('lte');
  whereField.in = createOperator('in');
  whereField.nin = createOperator('nin');
  whereField.notIn = createOperator('notIn');
  whereField.contains = createOperator('contains');
  return whereField;
};
const createConjuctionField = conjunction => {
  return (...statements) => {
    return [conjunction, 'conj', statements];
  };
};
const createWhere = () => {
  const proxy = new Proxy({}, {
    get(_, fieldName) {
      switch (fieldName) {
        case '$type':
          return 'where';
        case 'and':
        case 'or':
          return createConjuctionField(fieldName);
        default:
          return createWhereField(fieldName);
      }
    }
  });
  return proxy;
};
var createWhere$1 = createWhere;

const createSelect = ({
  alias,
  fields,
  name,
  on
} = {}) => {
  const fn = cb => {
    if (typeof cb === 'function') {
      const fields = cb(proxy);
      return createSelect({
        name,
        fields
      });
    }
    if (cb && typeof cb === 'object' && typeof cb.rename === 'function') {
      return cb.rename(name);
    }
    throw new Error(`Invalid field callback provided: [${typeof cb}]`);
  };
  const proxy = new Proxy(fn, {
    get(_, fieldName) {
      switch (fieldName) {
        case 'as':
          return v => {
            return createSelect({
              name,
              fields,
              alias: v
            });
          };
        case 'on':
          return (v, cb) => {
            const fields = cb(proxy);
            return createSelect({
              name,
              fields,
              alias,
              on: v
            });
          };
        case '$type':
          return 'select';
        case '$on':
          return on;
        case '$alias':
          return alias;
        case '$name':
          return name;
        case '$fields':
          return fields;
        default:
          return createSelect({
            name: fieldName
          });
      }
    }
  });
  return proxy;
};
var createSelect$1 = createSelect;

const stringifySelect = element => {
  if (element.$type === 'query') {
    return element.stringify();
  }
  const output = [];
  const {
    $alias,
    $fields,
    $name,
    $on
  } = element;
  if ($name) {
    if ($alias) {
      output.push($alias, ': ');
    }
    output.push($name);
  }
  if ($on) {
    output.push('... on ', $on, ' ');
  }
  if ($fields) {
    output.push('{\n', $fields.map(stringifySelect).join('\n'), '\n}');
  }
  return output.join('');
};
var stringifySelect$1 = stringifySelect;

const stringifyPrimative = value => {
  switch (typeof value) {
    case 'string':
      // If the value is an address, we want to normalize it
      // Often we hit issues where the subgraph has lowercase addresses but we're passing uppercase values in
      // resulting in 0 results
      value = normalizeIfAddress(value);
      break;
    case 'bigint':
      // JSON.stringify doesn't work on bigints so we need to handle them separately
      return value.toString();
  }

  // Stringify the primitive value
  return JSON.stringify(value);
};
const stringifyStatementKey = statement => {
  const [field, operator, value] = statement;
  switch (operator) {
    case 'is':
      if (value && typeof value === 'object') {
        return `${field}_`;
      }
      return `${field}`;
    case 'ne':
      return `${field}_ne`;
    case 'not':
      return `${field}_not`;
    case 'in':
      return `${field}_in`;
    case 'nin':
      return `${field}_nin`;
    case 'notIn':
      return `${field}_not_in`;
    case 'conj':
      return `${field}`;
    case 'gt':
    case 'gte':
    case 'lt':
    case 'lte':
    default:
      return `${field}_${operator}`;
  }
};
const hasDuplicateKeys = statements => {
  if (statements.length < 2) {
    return false;
  }
  const keys = new Set();
  for (const statement of statements) {
    const key = stringifyStatementKey(statement);
    if (keys.has(key)) {
      return true;
    }
    keys.add(key);
  }
  return false;
};
const hasMixedOrStatements = statements => {
  if (statements.length < 2) {
    return false;
  }
  for (const [field] of statements) {
    if (field === 'or') {
      return true;
    }
  }
  return false;
};
const stringifyAnd = statements => {
  const keys = new Set();
  const groupedStatements = [[]];

  // We can group all statements in same object if they have separate keys
  // Any duplicate keys will need to be in separate objects
  statements.forEach(statement => {
    const key = stringifyStatementKey(statement);
    let statements = groupedStatements[0];
    if (keys.has(key) || key === 'or') {
      statements = [];
      groupedStatements.push(statements);
    } else {
      keys.add(key);
    }
    statements.push(statement);
  });
  const x = groupedStatements.map(statements => stringifyWhere(statements)).filter(Boolean);
  if (!x.length) {
    return '';
  }
  const y = x.join('\n},\n{\n');
  return `and: [\n{\n${y}\n}\n]\n`;
};
const stringifyOr = statements => {
  const x = statements.map(s => stringifyWhere([s])).filter(Boolean);
  if (!x.length) {
    return '';
  }
  const y = x.join('\n},\n{\n');
  return `or: [\n{\n${y}\n}\n]\n`;
};
const stringifyConjuction = (conjunction, statements) => {
  switch (conjunction) {
    case 'and':
      return stringifyAnd(statements);
    case 'or':
      return stringifyOr(statements);
    default:
      return '';
  }
};
const stringifyWhere = statements => {
  if (hasDuplicateKeys(statements) || hasMixedOrStatements(statements)) {
    statements = [createConjuctionField('and')(...statements)];
  }
  return statements.map(([field, operator, value]) => {
    if (value == null) {
      return '';
    }
    const key = stringifyStatementKey([field, operator, value]);
    switch (operator) {
      case 'is':
        if (value && typeof value === 'object') {
          const w = stringifyWhere(value);
          if (!w) {
            return '';
          }
          return `${key}: {\n${w}\n}`;
        }
        return `${key}: ${stringifyPrimative(value)}`;
      case 'isNot':
        if (Array.isArray(value)) {
          return `${key}: ${stringifyPrimative(value)}`;
        }
        return `${key}: ${stringifyPrimative(value)}`;
      case 'in':
        return `${key}: [${value.map(stringifyPrimative)}]`;
      case 'conj':
        return stringifyConjuction(field, value);
      case 'gt':
      case 'gte':
      case 'lt':
      case 'lte':
      default:
        return `${key}: ${stringifyPrimative(value)}`;
    }
  }).filter(Boolean).join(',\n');
};
var stringifyWhere$1 = stringifyWhere;

const stringifyQuery = args => {
  if (!args.select?.length) {
    throw new Error(`Select must be specified`);
  }
  const output = [];

  // Collection
  if (args.alias) {
    output.push(args.alias, ': ');
  }
  output.push(args.name, ' ');
  const w = args.where && stringifyWhere$1(args.where);
  // Filters
  if (args.first || args.skip || args.orderBy || args.orderDirection || w || args.id) {
    output.push('(\n');
    if (args.first) {
      output.push('first: ', `${args.first}`, '\n');
    }
    if (args.skip) {
      output.push('skip: ', `${args.skip}`, '\n');
    }
    if (args.orderBy) {
      output.push('orderBy: ', args.orderBy, '\n');
    }
    if (args.orderDirection) {
      output.push('orderDirection: ', args.orderDirection, '\n');
    }
    if (args.id) {
      output.push('id: ', `"${normalizeIfAddress(args.id)}"`, '\n');
    }
    // Where
    if (w) {
      output.push('where: ', '{\n', w, '\n}', '\n');
    }
    output.push(')');
  }
  // Select
  output.push('{\n');
  output.push(args.select.map(s => {
    switch (s.$type) {
      case 'query':
        return s.stringify();
      case 'select':
        return stringifySelect$1(s);
      default:
        return s.toString();
    }
  }).join('\n'));
  output.push('\n}');
  return output.join('');
};
var stringifyQuery$1 = stringifyQuery;

const prettyFormat = str => {
  let indent = 0;
  const lines = str.split('\n').filter(Boolean);
  const text = lines.map(line => {
    if (line.includes('}')) {
      indent -= 2;
    }
    if (line.includes(')')) {
      indent -= 2;
    }
    if (line.includes(']')) {
      indent -= 2;
    }
    const newline = ' '.repeat(indent) + line;
    if (line.includes('{')) {
      indent += 2;
    }
    if (line.includes('(')) {
      indent += 2;
    }
    if (line.includes('[')) {
      indent += 2;
    }
    return newline;
  }).join('\n');
  return text;
};
var prettyFormat$1 = prettyFormat;

const combineQueries = (q1, q2) => {
  const combined = {
    toString() {
      const content = combined.stringify();
      return ['{', content, '}'].join('\n');
    },
    stringify() {
      return [q1, q2].map(q => {
        return q.stringify();
      }).join('\n\n');
    },
    combine(q3) {
      return combineQueries(combined, q3);
    }
  };
  return combined;
};
const createQueryObj = args => {
  const query = {
    first: limit => {
      return createQueryObj({
        ...args,
        first: limit
      });
    },
    skip: limit => {
      return createQueryObj({
        ...args,
        skip: limit
      });
    },
    orderBy: field => {
      return createQueryObj({
        ...args,
        orderBy: field
      });
    },
    orderDirection: orderDirection => {
      return createQueryObj({
        ...args,
        orderDirection
      });
    },
    id: id => {
      return createQueryObj({
        ...args,
        id
      });
    },
    where: cb => {
      const whereApi = createWhere$1();
      let where = cb(whereApi);
      if (!Array.isArray(where[0])) {
        where = [where];
      }
      return createQueryObj({
        ...args,
        where
      });
    },
    select: cb => {
      const proxy = createSelect$1();
      const select = cb(proxy);
      return createQueryObj({
        ...args,
        select
      });
    },
    rename: newName => {
      return createQueryObj({
        ...args,
        name: newName
      });
    },
    as: newName => {
      return createQueryObj({
        ...args,
        alias: newName
      });
    },
    stringify: () => {
      return stringifyQuery$1(args);
    },
    toString: () => {
      const str = query.stringify();
      const wrapped = ['{', str, '}'].join('\n');
      return prettyFormat$1(wrapped);
    },
    combine: q => {
      return combineQueries(query, q);
    },
    $type: 'query'
  };
  return query;
};
const rootQuery = new Proxy({}, {
  get(_, key) {
    switch (key) {
      case '$type':
        return 'query';
      case 'combine':
        return ([firstQuery, ...queries]) => {
          return queries.reduce((master, query) => {
            return master.combine(query);
          }, firstQuery);
        };
      default:
        return createQueryObj({
          name: key
        });
    }
  }
});
const createQuery = () => rootQuery;
var createQuery$1 = createQuery;

export { QueryError, createQuery$1 as createGraphQuery, gql, sendQuery as query, queryGraph };
